import {
  Box,
  Button,
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import styles from "../styles";
import theme from "../../../../theme/Theme";
import CrownIcon from "../../../../assets/svg/CrownIcon";
import { TrophyCup } from "../../../../assets";
import { formatNumber } from "../../../../utils/common";
import { UserIcon } from "@heroicons/react/24/solid";

const TopWinnersComponents = ({ drawDetailData, tickerUnit }) => {
  const navigate = useNavigate();
  const matches = useMediaQuery(theme.breakpoints.up("sm"));
  const [rewardData, setRewardData] = useState([]);

  useEffect(() => {
    if (drawDetailData?.winnerLevels) {
      setRewardData(drawDetailData.winnerLevels);
    }
  }, [drawDetailData?.winnerLevels]);

  const getPrizeAmount = (percentage) => {
    if (percentage && drawDetailData) {
      return (
        (drawDetailData.maxParticipants *
          percentage *
          drawDetailData.ticketPrice) /
        100
      );
    }
    return 0;
  };

  const reorderedRewards =
    rewardData.length >= 3
      ? [rewardData[1], rewardData[0], rewardData[2]]
      : rewardData;
  const rankNumbers = [2, 1, 3];
  const crownColors = [
    "rgba(192, 192, 192, 1)",
    "rgba(255, 101, 14, 1)",
    "rgba(222, 70, 30, 1)",
  ];

  return (
    <Box sx={styles.topRewardsContainer}>
      <Box sx={styles.topRewardsBg} />
      <Box sx={styles.topRewards}>
        <Box sx={styles.topRewardsTitle}>
          <Typography sx={styles.topRewardsTitleText}>
            Top <span style={styles.topRewardsTitleHighlight}>3 Rewards</span>
          </Typography>
        </Box>

        <Stack
          direction={{ xs: "column", md: "row", lg: "row" }}
          justifyContent="center"
          spacing={{ xs: "5vw", md: "10vw", lg: "15vw" }}
          sx={{ margin: "2em auto", paddingRight: "15vw" }}
        >
          {reorderedRewards.map((reward, index) => (
            <Box
              key={index}
              sx={styles.rewardCrownBox}
              pt={index === 1 ? 0 : 4}
            >
              <CrownIcon width={91} height={67} color={crownColors[index]} />
              <Box
                sx={{
                  ...styles.rewardRankRing,
                  border: `3px solid ${crownColors[index]}`,
                }}
              >
                <Typography sx={styles.rewardRankText}>
                  {rankNumbers[index]}
                </Typography>
              </Box>
              <Box sx={styles.rewardAmountContainer}>
                <Typography sx={styles.rewardAmountText}>
                  {`${drawDetailData.currencyTicker} $${formatNumber(
                    getPrizeAmount(reward?.percentage)
                  )}`}
                </Typography>
              </Box>
            </Box>
          ))}
        </Stack>

        <Divider sx={{ pt: 8, mb: 4, mx: 8 }} />
        <TableContainer sx={{ marginBottom: "1em" }}>
          <Table sx={{ border: "none" }}>
            <TableHead>
              <TableRow sx={{ border: "none" }}>
                <TableCell align="center" sx={{ border: "none" }}>
                  <Typography sx={styles.tableHeadText}>Level</Typography>
                </TableCell>
                <TableCell align="center" sx={{ border: "none" }}>
                  <Typography sx={styles.tableHeadText}>Pool Prize</Typography>
                </TableCell>
                <TableCell align="center" sx={{ border: "none" }}>
                  <Typography sx={styles.tableHeadText}>
                    Winner Prize
                  </Typography>
                </TableCell>
                <TableCell align="center" sx={{ border: "none" }}>
                  <Typography sx={styles.tableHeadText}>
                    Total Winners
                  </Typography>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rewardData?.slice(3, rewardData?.length)?.map((item, index) => (
                <TableRow
                  key={index}
                  sx={{
                    backgroundColor:
                      index % 2 !== 0
                        ? "rgba(242, 242, 242, 1)"
                        : "rgba(255, 255, 255, 1)",
                  }}
                >
                  <TableCell align="center" sx={{ border: "none" }}>
                    <Typography sx={styles.tableCellText}>
                      {item.level}
                    </Typography>
                  </TableCell>

                  <TableCell align="center" sx={{ border: "none" }}>
                    <Typography
                      sx={styles.tableCellText}
                    >{`${tickerUnit} $${formatNumber(
                      item.poolPrize
                    )}`}</Typography>
                  </TableCell>
                  <TableCell align="center" sx={{ border: "none" }}>
                    <Typography
                      sx={styles.tableCellText}
                    >{`${tickerUnit} $${formatNumber(
                      item.winnerPrize
                    )}`}</Typography>
                  </TableCell>
                  <TableCell align="center" sx={{ border: "none" }}>
                    <Typography sx={styles.tableCellText}>
                      {item.winners}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
      <Box sx={styles.winProbabilityContainer}>
        <Stack
          direction={"row"}
          alignItems={"center"}
          justifyContent={"space-between"}
          spacing={0}
        >
          <Box
            component="img"
            src={TrophyCup}
            alt="TrophyCup"
            style={matches ? styles.trophyCupImage : styles.trophyCupImageSm}
          />
          <Box>
            <Typography sx={styles.probabilityTitleText}>
              Win Probability
            </Typography>
            <Typography sx={styles.probabilityValueText} mt={1}>
              {drawDetailData.winningProbability}%
            </Typography>
          </Box>
        </Stack>
        <Divider
          orientation="vertical"
          flexItem
          sx={{ color: "rgba(192, 192, 192, 1)" }}
        />
        <Box>
          <Typography sx={styles.probabilityTitleText}>
            Number of Winners
          </Typography>
          <Stack
            direction={"row"}
            alignItems={"center"}
            justifyContent={"center"}
            mt={1}
            spacing={{ xs: 1, lg: 2 }}
          >
            {matches ? (
              <UserIcon width={50} height={50} />
            ) : (
              <UserIcon width={20} height={20} />
            )}

            <Typography sx={styles.probabilityValueText}>
              {drawDetailData.totalWinners}
            </Typography>
          </Stack>
        </Box>
      </Box>

      <Typography sx={styles.declaimerText}>
        Disclaimer: The number of winners and prize amounts may change based on
        participation levels. Please note that withdrawal fees will apply to
        this draw. For more details, visit our{" "}
        <Link style={styles.disclaimerLink} to="/disclaimer">
          Disclaimer Page
        </Link>
        .
      </Typography>
      <Box sx={styles.referralButtonContainer}>
        <Button
          variant="contained"
          sx={styles.referralButtonStyle}
          onClick={() => {
            navigate("/referrals");
          }}
        >
          REFERRAL LINK
        </Button>
      </Box>
    </Box>
  );
};

export default TopWinnersComponents;
